import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type {
  RechargeDevice,
  RechargePlan,
  RechargeResult,
} from "./subscriberRecharge";

/**
 * The door collector's phone page (owner 2026-11-02, radius-wiki
 * door-collection-procedure.md) -- radaccess /access/operator/{ns}/collect/*,
 * RBAC resource `collect`, held by the collector role only. A collector sees
 * the areas it works and their customers (mobiles masked), takes cash (with a
 * cash code where switched on) or a UPI payment shown as a QR, and sees its
 * own day. Paths are relative to gatewayBase, which already ends in /access.
 */
export interface CollectArea {
  id: string;
  name: string;
}

export interface CollectCustomer {
  accountid: string;
  name: string;
  username: string;
  /** Last 4 digits only. */
  mobile: string;
  mobile_verified: boolean;
  card_no: string;
  area: string;
  /** IST "YYYY-MM-DD HH:MM", "" when none. */
  expiry: string;
}

export interface CollectCustomerDetail {
  devices: RechargeDevice[];
  plans: RechargePlan[];
  card_no: string;
}

export interface CollectPayment {
  groupname: string;
  amount: number;
  method: "cash" | "upi";
  reference: string;
  requestid: string;
  card_no?: string;
  cash_code?: string;
}

export interface Collection {
  receiptno: string;
  method: string;
  amount: number;
  reference: string;
  card_no: string;
  check_status: string;
  handed_over: number;
  created_at: string;
}

export interface CollectToday {
  collections: Collection[];
  totals: Record<string, number>;
}

const at = (ns: string) => `/operator/${encodeURIComponent(ns)}/collect`;

export async function collectAreas(
  identity: CallerIdentity,
  ns: string,
  signal?: AbortSignal,
): Promise<CollectArea[]> {
  return (
    await callGateway<CollectArea[]>(
      identity,
      config().gatewayBase,
      `${at(ns)}/areas`,
      signal ? { signal } : {},
    )
  ).data;
}

export async function collectCustomers(
  identity: CallerIdentity,
  ns: string,
  areaId: string,
  signal?: AbortSignal,
): Promise<CollectCustomer[]> {
  return (
    await callGateway<CollectCustomer[]>(
      identity,
      config().gatewayBase,
      `${at(ns)}/areas/${encodeURIComponent(areaId)}/customers`,
      signal ? { signal } : {},
    )
  ).data;
}

export async function collectCustomer(
  identity: CallerIdentity,
  ns: string,
  accountId: string,
  signal?: AbortSignal,
): Promise<CollectCustomerDetail> {
  return (
    await callGateway<CollectCustomerDetail>(
      identity,
      config().gatewayBase,
      `${at(ns)}/customers/${encodeURIComponent(accountId)}`,
      signal ? { signal } : {},
    )
  ).data;
}

export async function collectPayment(
  identity: CallerIdentity,
  ns: string,
  accountId: string,
  input: CollectPayment,
): Promise<RechargeResult> {
  return (
    await callGateway<RechargeResult>(
      identity,
      config().gatewayBase,
      `${at(ns)}/customers/${encodeURIComponent(accountId)}/recharge`,
      { method: "POST", body: input },
    )
  ).data;
}

export async function collectToday(
  identity: CallerIdentity,
  ns: string,
  signal?: AbortSignal,
): Promise<CollectToday> {
  return (
    await callGateway<CollectToday>(
      identity,
      config().gatewayBase,
      `${at(ns)}/today`,
      signal ? { signal } : {},
    )
  ).data;
}

/** A login that holds the collector role: it sees only the phone page. */
export function isCollector(roles: string[]): boolean {
  return roles.some((r) => r.toLowerCase() === "collector");
}

/** Customers whose name, login, card or last 4 digits match what was typed. */
export function matchCustomers(
  list: CollectCustomer[],
  q: string,
): CollectCustomer[] {
  const s = q.trim().toLowerCase();
  if (!s) return list;
  return list.filter((c) =>
    [c.name, c.username, c.card_no, c.mobile].some((v) =>
      v.toLowerCase().includes(s),
    ),
  );
}

/** The paths, for the test. */
export const collectPaths = { at };
